/**
 * Typed API error surface, framework-free so tests and non-fetch callers can
 * import it without dragging in the auth/runtime dependencies of `api.ts`.
 */

/**
 * The scale boundary named by a 402 `upgrade_required` problem (#59). The names are the server's
 * stable contract (ADR-007 §1) — `ai_plan_modification` and `ai_quota` joined in M24.
 */
export type UpgradeBoundary =
  | "second_active_plan"
  | "external_sync"
  | "coaching_intensity"
  | "ai_plan_modification"
  | "ai_quota"

/** Thrown for non-2xx responses; carries the HTTP status for UI handling. */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    /** Machine-readable problem code, e.g. "upgrade_required" on a free-tier scale gate (#59). */
    public readonly code?: string,
    /** Which scale boundary was hit when `code === "upgrade_required"`. */
    public readonly boundary?: string
  ) {
    super(message)
    this.name = "ApiError"
  }
}

/** True when the error is the free-tier scale gate's 402 — render the upgrade prompt, not a failure. */
export function isUpgradeRequired(
  error: unknown
): error is ApiError & { code: "upgrade_required" } {
  return error instanceof ApiError && error.code === "upgrade_required"
}

/**
 * Pull the machine-readable `code`/`boundary` extension members out of an RFC-9457
 * problem-details body, tolerating non-JSON error bodies.
 */
export function extractProblemFields(body: string): {
  code?: string
  boundary?: string
} {
  try {
    const problem = JSON.parse(body) as { code?: unknown; boundary?: unknown }
    return {
      code: typeof problem?.code === "string" ? problem.code : undefined,
      boundary:
        typeof problem?.boundary === "string" ? problem.boundary : undefined,
    }
  } catch {
    return {}
  }
}
